import { useId, useState, type ChangeEvent, type ReactNode } from 'react';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Button, { type ButtonProps } from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import InputBase from '@mui/material/InputBase';
import { IconAlertCircle, IconCheck, IconChevronDown, IconSearch } from '@tabler/icons-react';
import { tint, v } from './format';

type Kind = 'primary' | 'secondary' | 'ghost' | 'danger' | 'solid-danger';

/**
 * Button in the five prototype kinds. Pass `disabledReason` to disable it and explain why: the reason is
 * reachable by hover and keyboard focus (disabled buttons don't receive pointer events themselves).
 */
export function Btn({ kind = 'secondary', icon, children, sx, disabledReason, disabled, ...rest }: { kind?: Kind; icon?: ReactNode; disabledReason?: string | undefined } & Omit<ButtonProps, 'variant'>) {
  const styles: Record<Kind, object> = {
    primary: { background: v('button'), color: '#fff', boxShadow: `0 0 0 1px ${v('buttonRing')} inset`, '&:hover': { background: v('accent') } },
    secondary: { background: v('panel'), color: v('text'), border: `1px solid ${v('border2')}`, '&:hover': { background: v('sunk') } },
    ghost: { background: 'transparent', color: v('dim'), px: '10px', '&:hover': { background: v('sunk') } },
    danger: { background: tint(v('danger'), 8), color: v('danger'), border: `1px solid ${tint(v('danger'), 35)}`, '&:hover': { background: tint(v('danger'), 14) } },
    'solid-danger': { background: v('dangerSolid'), color: '#fff', '&:hover': { background: v('dangerSolid'), filter: 'brightness(1.08)' } },
  };
  const button = (
    <Button disableElevation startIcon={icon} disabled={disabled || Boolean(disabledReason)} sx={{ ...styles[kind], '&.Mui-disabled': { opacity: 0.45, color: 'inherit' }, ...sx }} {...rest}>
      {children}
    </Button>
  );
  if (!disabledReason) return button;
  return (
    <Tooltip title={disabledReason}>
      <Box component="span" tabIndex={0} aria-label={disabledReason} sx={{ display: 'inline-flex', borderRadius: '9px', cursor: 'not-allowed', '&:focus-visible': { outline: `2px solid ${v('accent')}`, outlineOffset: 2 } }}>
        {button}
      </Box>
    </Tooltip>
  );
}

/** Dropdown select styled as the prototype filter button: "Label Value ▾". */
export function FilterSelect<T extends string>({
  label, value, options, onChange, icon, describe,
}: { label?: string; value: T; options: readonly T[]; onChange: (v: T) => void; icon?: ReactNode; describe?: (o: T) => ReactNode }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const id = useId();
  return (
    <>
      <Box
        component="button"
        type="button"
        aria-haspopup="menu"
        aria-controls={anchor ? id : undefined}
        aria-expanded={Boolean(anchor)}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: '7px', height: 34, px: '11px', border: `1px solid ${v('border2')}`,
          borderRadius: '8px', background: v('sunk'), fontFamily: 'inherit', fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap',
          color: v('text'), '&:focus-visible': { outline: `2px solid ${v('accent')}`, outlineOffset: 2 },
        }}
      >
        {icon && <Box component="span" sx={{ display: 'flex', color: v('caption') }}>{icon}</Box>}
        {label && <Box component="span" sx={{ color: v('caption'), fontWeight: 500 }}>{label}</Box>}
        <Box component="span" sx={{ fontWeight: 600 }}>{value}</Box>
        <IconChevronDown size={13} stroke={2.4} color="var(--ix-caption)" />
      </Box>
      <Menu id={id} anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} slotProps={{ list: { dense: true, sx: { p: '5px', minWidth: 190 } } }}>
        {options.map((o) => (
          <MenuItem
            key={o}
            selected={o === value}
            onClick={() => { onChange(o); setAnchor(null); }}
            sx={{ borderRadius: '7px', fontSize: 13, gap: '10px', minHeight: 34 }}
          >
            <Box sx={{ width: 14, display: 'flex' }}>{o === value && <IconCheck size={14} stroke={2.6} color="var(--ix-accentText)" />}</Box>
            <Box>
              <Box>{o}</Box>
              {describe && <Box sx={{ fontSize: 11.5, color: v('caption') }}>{describe(o)}</Box>}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** Segmented control with optional counts (status tabs). */
export function Segmented<T extends string>({
  options, value, onChange, counts, label,
}: { options: readonly T[]; value: T; onChange: (v: T) => void; counts?: Partial<Record<T, ReactNode>>; label?: string }) {
  return (
    <Box role="group" aria-label={label} sx={{ display: 'inline-flex', gap: '2px', p: '3px', background: v('sunk'), border: `1px solid ${v('border')}`, borderRadius: '10px', flexWrap: 'wrap' }}>
      {options.map((o) => {
        const on = o === value;
        return (
          <Box
            key={o}
            component="button"
            type="button"
            aria-pressed={on}
            onClick={() => onChange(o)}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: '7px', height: 30, px: '11px', border: 0, borderRadius: '8px',
              fontFamily: 'inherit', fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: on ? 600 : 500,
              background: on ? v('panel') : 'transparent', color: on ? v('text') : v('dim'),
              boxShadow: on ? `0 0 0 1px ${v('border2')} inset` : 'none',
              '&:focus-visible': { outline: `2px solid ${v('accent')}`, outlineOffset: 1 },
            }}
          >
            {o}
            {counts?.[o] !== undefined && (
              <Box component="span" className="ix-mono" sx={{ fontSize: 11, px: '6px', py: '1px', borderRadius: '5px', background: on ? tint(v('accent'), 16) : v('panel'), color: on ? v('accentText') : v('caption') }}>
                {counts[o]}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

/** Underline tabs (Settings, detail pages). */
export function UnderlineTabs<T extends string>({ options, value, onChange }: { options: readonly T[]; value: T; onChange: (v: T) => void }) {
  // Tabs wrap onto another line rather than scroll sideways: every tab stays visible, with no scrollbar.
  return (
    <Box role="tablist" sx={{ display: 'flex', flexWrap: 'wrap', columnGap: '22px', rowGap: 0, borderBottom: `1px solid ${v('border')}`, position: 'relative' }}>
      {options.map((o) => {
        const on = o === value;
        return (
          <Box
            key={o}
            role="tab"
            component="button"
            type="button"
            aria-selected={on}
            onClick={() => onChange(o)}
            sx={{
              border: 0, background: 'transparent', fontFamily: 'inherit', fontSize: 13.5, cursor: 'pointer', whiteSpace: 'nowrap',
              px: 0, py: '11px', fontWeight: on ? 600 : 500, color: on ? v('text') : v('dim'),
              boxShadow: on ? `inset 0 -2px 0 ${v('accent')}` : 'none',
              '&:focus-visible': { outline: `2px solid ${v('accent')}`, outlineOffset: 2 },
            }}
          >
            {o}
          </Box>
        );
      })}
    </Box>
  );
}

export function Toggle({ checked, onChange, label, disabled = false }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean | undefined }) {
  return (
    <Switch
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      slotProps={{ input: { 'aria-label': label } }}
      size="small"
      sx={{
        '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: v('button'), opacity: 1 },
        '& .MuiSwitch-track': { backgroundColor: v('border2'), opacity: 1 },
        '& .Mui-disabled + .MuiSwitch-track': { opacity: '0.5 !important' },
      }}
    />
  );
}

/** Toggle with a visible label and optional caption. */
export function SwitchRow({ checked, onChange, label, sub, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; sub?: ReactNode; disabled?: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <Toggle checked={checked} onChange={onChange} label={label} disabled={disabled} />
      <Box>
        <Box sx={{ fontSize: 13.5, fontWeight: 600, color: disabled ? v('dim') : v('text') }}>{label}</Box>
        {sub && <Box sx={{ fontSize: 12, color: v('caption') }}>{sub}</Box>}
      </Box>
    </Box>
  );
}

export function SearchField({ value, onChange, placeholder, width = 300 }: { value: string; onChange: (v: string) => void; placeholder: string; width?: number | string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: 34, px: '11px', width, maxWidth: '100%', border: `1px solid ${v('border2')}`, borderRadius: '8px', background: v('sunk') }}>
      <IconSearch size={14} color="var(--ix-caption)" />
      <InputBase
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputProps={{ 'aria-label': placeholder }}
        sx={{ flex: 1, fontSize: 12.5, color: v('text'), '& input::placeholder': { color: v('caption'), opacity: 1 } }}
      />
    </Box>
  );
}

/**
 * Form field: label, control, and a hint or error below. The message gets id `${htmlFor}-msg`; pass the
 * same id to the control's `describedBy` so screen readers announce it.
 */
export function Field({ label, hint, error, children, htmlFor, required }: { label: ReactNode; hint?: ReactNode; error?: string | undefined; children: ReactNode; htmlFor?: string; required?: boolean }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px', minWidth: 0 }}>
      <Box component="label" htmlFor={htmlFor} sx={{ fontSize: 13, fontWeight: 600, color: v('text') }}>
        {label}{required && <Box component="span" aria-hidden sx={{ color: v('danger') }}> *</Box>}
      </Box>
      {children}
      {error ? (
        <Box id={htmlFor ? `${htmlFor}-msg` : undefined} role="alert" sx={{ fontSize: 12, color: v('danger'), display: 'flex', gap: '5px', alignItems: 'center' }}>
          <IconAlertCircle size={13} aria-hidden />{error}
        </Box>
      ) : hint ? <Box id={htmlFor ? `${htmlFor}-msg` : undefined} sx={{ fontSize: 12, color: v('caption') }}>{hint}</Box> : null}
    </Box>
  );
}

/** Shared input frame (used by TextInput, TextArea, MoneyInput). */
export const inputSx = (invalid = false) => ({
  px: '12px', border: `1px solid ${invalid ? v('danger') : v('border2')}`, borderRadius: '10px', background: v('sunk'), color: v('text'), fontSize: 14, width: '100%',
  '&.Mui-focused': { borderColor: invalid ? v('danger') : v('accent') },
  '&.Mui-disabled': { background: 'transparent', borderStyle: 'dashed' },
  '& .Mui-disabled': { WebkitTextFillColor: v('dim'), color: v('dim') },
  '& input::placeholder, & textarea::placeholder': { color: v('caption'), opacity: 1 },
});

export interface InputProps {
  id?: string | undefined; value: string; onChange: (v: string) => void; placeholder?: string | undefined; mono?: boolean | undefined; type?: string | undefined;
  disabled?: boolean | undefined; readOnly?: boolean | undefined; invalid?: boolean | undefined; maxLength?: number | undefined; list?: string | undefined;
  describedBy?: string | undefined; autoFocus?: boolean | undefined;
  inputMode?: 'text' | 'decimal' | 'numeric' | 'email' | 'tel' | 'url' | 'search' | undefined;
  /** e.g. "off" for a provider secret, so the browser neither fills nor saves it. */
  autoComplete?: string | undefined;
}

export function TextInput({ id, value, onChange, placeholder, mono = false, type = 'text', disabled, readOnly, invalid = false, maxLength, list, describedBy, autoFocus, inputMode, autoComplete }: InputProps) {
  return (
    <InputBase
      id={id}
      type={type}
      value={value}
      disabled={disabled}
      readOnly={readOnly}
      error={invalid}
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={mono ? 'ix-mono' : undefined}
      inputProps={{ maxLength, list, inputMode, 'aria-invalid': invalid || undefined, 'aria-describedby': describedBy }}
      sx={{ ...inputSx(invalid), height: 42 }}
    />
  );
}

/** Multi-line text input in the same style as TextInput. */
export function TextArea({ id, value, onChange, placeholder, rows = 3, mono = false, disabled, readOnly, invalid = false, describedBy }: Omit<InputProps, 'type' | 'list' | 'inputMode' | 'autoFocus' | 'maxLength'> & { rows?: number }) {
  return (
    <InputBase
      id={id}
      multiline
      minRows={rows}
      value={value}
      disabled={disabled}
      readOnly={readOnly}
      error={invalid}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={mono ? 'ix-mono' : undefined}
      inputProps={{ 'aria-invalid': invalid || undefined, 'aria-describedby': describedBy }}
      sx={{ ...inputSx(invalid), py: '10px', lineHeight: 1.5, fontSize: mono ? 12.5 : 14 }}
    />
  );
}

export interface Opt { value: string; label: string; disabled?: boolean }
/** ["A","B"] -> [{ value: "A", label: "A" }, ...] */
export const opts = (values: readonly string[]): Opt[] => values.map((x) => ({ value: x, label: x }));

/** Native select (best keyboard and screen-reader support). Options are strings or { value, label, disabled }. */
export function NativeSelect({ id, value, onChange, options, disabled, invalid = false, describedBy }: { id?: string | undefined; value: string; onChange: (v: string) => void; options: readonly (string | Opt)[]; disabled?: boolean | undefined; invalid?: boolean | undefined; describedBy?: string | undefined }) {
  return (
    <Box
      component="select"
      id={id}
      value={value}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
      sx={{
        height: 42, px: '12px', border: `1px solid ${invalid ? v('danger') : v('border2')}`, borderRadius: '10px', background: v('sunk'), color: v('text'),
        fontFamily: 'inherit', fontSize: 14, width: '100%',
        '&:disabled': { background: 'transparent', borderStyle: 'dashed', color: v('dim'), opacity: 1 },
        '&:focus-visible': { outline: `2px solid ${v('accent')}`, outlineOffset: 1 },
      }}
    >
      {options.map((o) => (typeof o === 'string'
        ? <option key={o} value={o}>{o}</option>
        : <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>))}
    </Box>
  );
}
